import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, CheckCircle, XCircle, Trash2, Gift, Eye, EyeOff, CreditCard, MessageSquare, FileText, Quote, Download, MapPin, BarChart3, Building2, Award, ShieldCheck, Inbox, Briefcase, DollarSign, Sparkles, Share2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    fetchAllTours();
  }, []);

  const fetchAllTours = async () => {
    try {
      // Use admin endpoint to get all tours including unpublished
      const response = await axios.get(`${API}/admin/tours`);
      setTours(response.data);
    } catch (error) {
      toast.error('Failed to load tours');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const response = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.download = `audiostride-backup-${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Backup downloaded! ${response.data.summary.total_tours} tours, ${response.data.summary.total_stops} stops saved.`);
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleToggleStatus = async (tourId, currentStatus) => {
    try {
      await axios.post(`${API}/admin/tours/${tourId}/toggle-status`);
      const newStatus = !currentStatus;
      toast.success(newStatus ? 'Tour enabled (visible to users)' : 'Tour disabled (hidden from users)');
      fetchAllTours();
    } catch (error) {
      toast.error('Failed to update tour status');
    }
  };

  const handleDeleteTour = async (tourId, tourTitle) => {
    const confirmed = window.confirm(
      `Are you sure you want to DELETE "${tourTitle}"?\n\nThis will permanently delete the tour and all its stops. This action cannot be undone.\n\nTip: Use "Disable" to hide the tour without deleting it.`
    );

    if (!confirmed) return;

    try {
      await axios.delete(`${API}/admin/tours/${tourId}`);
      toast.success('Tour deleted successfully');
      fetchAllTours();
    } catch (error) {
      toast.error('Failed to delete tour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-4 font-sans">Admin Dashboard</h1>
          <p className="text-slate-300 text-lg font-body">Manage your audio tours</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-slate-900 font-sans">All Tours</h2>
          <div className="flex gap-4 flex-wrap">
            <button
              onClick={() => navigate('/admin/blog')}
              data-testid="blog-management-button"
              className="flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <FileText className="w-5 h-5 mr-2" />
              Blog
            </button>
            <button
              onClick={() => navigate('/admin/testimonials')}
              data-testid="testimonials-button"
              className="flex items-center bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Quote className="w-5 h-5 mr-2" />
              Testimonials
            </button>
            <button
              onClick={() => navigate('/admin/reviews')}
              data-testid="reviews-moderation-button"
              className="flex items-center bg-rose-500 hover:bg-rose-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <ShieldCheck className="w-5 h-5 mr-2" />
              Reviews
            </button>
            <button
              onClick={() => navigate('/admin/badges')}
              data-testid="badges-button"
              className="flex items-center bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Award className="w-5 h-5 mr-2" />
              Badges
            </button>
            <button
              onClick={() => navigate('/admin/strides-rules')}
              data-testid="strides-rules-button"
              className="flex items-center bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Strides
            </button>
            <button
              onClick={() => navigate('/admin/partner-leads')}
              data-testid="partner-leads-button"
              className="flex items-center bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Inbox className="w-5 h-5 mr-2" />
              B2B Leads
            </button>
            <button
              onClick={() => navigate('/admin/contacts')}
              data-testid="contact-submissions-button"
              className="flex items-center bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Messages
            </button>
            <button
              onClick={() => navigate('/admin/payments')}
              data-testid="payment-management-button"
              className="flex items-center bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Payments
            </button>
            <button
              onClick={() => navigate('/admin/promo-codes')}
              data-testid="promo-codes-button"
              className="flex items-center bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Gift className="w-5 h-5 mr-2" />
              Promo Codes
            </button>
            <button
              onClick={() => navigate('/admin/upload-audio')}
              data-testid="upload-audio-button"
              className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Upload Audio
            </button>
            <button
              onClick={() => navigate('/admin/create-tour')}
              data-testid="create-new-tour-button"
              className="flex items-center bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Tour
            </button>
            <button
              onClick={() => navigate('/admin/test-settings')}
              data-testid="test-settings-button"
              className="flex items-center bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Test Settings
            </button>
            <button
              onClick={() => navigate('/admin/analytics')}
              data-testid="analytics-button"
              className="flex items-center bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Analytics
            </button>
            <button
              onClick={() => navigate('/admin/social-analytics')}
              data-testid="social-analytics-button"
              className="flex items-center bg-gradient-to-r from-teal-500 to-cyan-500 hover:brightness-110 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Social ROI
            </button>
            <button
              onClick={() => navigate('/admin/finance')}
              data-testid="finance-button"
              className="flex items-center bg-slate-900 hover:bg-black text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <DollarSign className="w-5 h-5 mr-2" />
              Finance
            </button>
            <button
              onClick={() => navigate('/admin/analytics-offers')}
              data-testid="analytics-offers-button"
              className="flex items-center bg-gradient-to-r from-violet-500 to-fuchsia-600 hover:opacity-90 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Briefcase className="w-5 h-5 mr-2" />
              B2B Subscriptions
            </button>
            <button
              onClick={() => navigate('/admin/report-deliveries')}
              data-testid="report-deliveries-button"
              className="flex items-center bg-gradient-to-r from-cyan-500 to-teal-600 hover:opacity-90 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Send className="w-5 h-5 mr-2" />
              Report Deliveries
            </button>
            <button
              onClick={() => navigate('/admin/organizations')}
              data-testid="organizations-button"
              className="flex items-center bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Organizations
            </button>
            <button
              onClick={handleDownloadBackup}
              disabled={downloadingBackup}
              data-testid="download-backup-button"
              className="flex items-center bg-slate-700 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans disabled:opacity-50"
            >
              <Download className="w-5 h-5 mr-2" />
              {downloadingBackup ? 'Downloading...' : 'Download Backup'}
            </button>
          </div>
        </div>

        {tours.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-600 text-lg mb-4 font-body">No tours created yet</p>
            <button
              onClick={() => navigate('/admin/create-tour')}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              Create Your First Tour
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {tours.map((tour) => (
              <div
                key={tour.tour_id}
                data-testid={`admin-tour-${tour.tour_id}`}
                className={`bg-white rounded-2xl shadow-card hover:shadow-floating transition-all overflow-hidden ${!tour.published ? 'border-2 border-dashed border-slate-300' : ''}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className={`text-2xl font-bold mb-1 font-sans ${!tour.published ? 'text-slate-500' : 'text-slate-900'}`}>
                        {tour.title}
                        {!tour.published && <span className="ml-2 text-sm font-normal text-slate-400">(Hidden)</span>}
                      </h3>
                      <p className="text-teal-600 font-body">{tour.city}</p>
                    </div>
                    <div>
                      {tour.published ? (
                        <span className="flex items-center px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold font-body">
                          <Eye className="w-4 h-4 mr-2" />
                          Visible
                        </span>
                      ) : (
                        <span className="flex items-center px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm font-semibold font-body">
                          <EyeOff className="w-4 h-4 mr-2" />
                          Hidden
                        </span>
                      )}
                    </div>
                  </div>

                  <p className={`mb-4 line-clamp-2 font-body ${!tour.published ? 'text-slate-400' : 'text-slate-600'}`}>{tour.description}</p>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider font-body">Duration</span>
                      <p className="text-slate-900 font-semibold font-body">{tour.duration}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider font-body">Base Price</span>
                      <p className="text-slate-900 font-semibold font-body">${tour.base_price.toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider font-body">Group Add-on</span>
                      <p className="text-slate-900 font-semibold font-body">${tour.group_addon_price.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => navigate(`/admin/edit-tour/${tour.tour_id}`)}
                      data-testid={`edit-button-${tour.tour_id}`}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-full font-semibold transition-all active:scale-95 font-sans flex items-center"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleStatus(tour.tour_id, tour.published)}
                      data-testid={`toggle-status-${tour.tour_id}`}
                      className={`px-6 py-2 rounded-full font-semibold transition-all active:scale-95 font-sans flex items-center ${
                        tour.published 
                          ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                          : 'bg-teal-500 hover:bg-teal-600 text-white'
                      }`}
                    >
                      {tour.published ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          Disable
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Enable
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteTour(tour.tour_id, tour.title)}
                      data-testid={`delete-button-${tour.tour_id}`}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full font-semibold transition-all active:scale-95 font-sans flex items-center"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;